import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControlLabel from "@mui/material/FormControlLabel"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import DialogHeader from "../components/DialogHeader"
import EmailWYSWIGEditor, {
  useEmailWYSWIGEditorState,
} from "../components/EmailWYSWIGEditor"
import {
  EMAIL_SIGNATURE_API_URL,
  buildSignaturePreviewHtml,
  savedSignatureHasContent,
} from "../components/emailSignatureUtils"
import {
  SubjectField,
  getEmailEditorHTML,
} from "../email-campaigns/edit-email-mixin"
import { useDialogContainer } from "../utils"

export default function SendEmailDirectlyDialog({ open, onClose, candidate }) {
  const [subject, setSubject] = useState("")
  const [includeSignature, setIncludeSignature] = useState(false)
  const [signaturePreviewHtml, setSignaturePreviewHtml] = useState("")
  const {
    editorReady,
    setEditorReady,
    insertLinkDialogOpen,
    setInsertLinkDialogOpen,
    editorRef,
  } = useEmailWYSWIGEditorState(open)
  const [isSending, setIsSending] = useState(false)
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  useEffect(() => {
    if (!open) {
      setSubject("")
      setIncludeSignature(false)
      setSignaturePreviewHtml("")
      return
    }

    axios
      .get(EMAIL_SIGNATURE_API_URL)
      .then(({ data }) => {
        setSignaturePreviewHtml(buildSignaturePreviewHtml(data))
        setIncludeSignature(savedSignatureHasContent(data))
      })
      .catch(() => {
        setSignaturePreviewHtml("")
        setIncludeSignature(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleSoftClose() {
    onClose()
  }

  function handleSendButtonClicked() {
    setIsSending(true)
    axios
      .post(`/workflows/api/email/direct/${candidate.id}/`, {
        subject,
        email_template: getEmailEditorHTML(editorRef),
        include_signature: includeSignature,
      })
      .then(({ data }) => onClose(data))
      .finally(() => setIsSending(false))
  }

  return (
    <Container open={open} onClose={handleSoftClose} anchor="right">
      <DialogHeader title="Send Email To Candidate" onClose={handleSoftClose} />
      <DialogContent>
        <Column gap={2}>
          <SubjectField subject={subject} setSubject={setSubject} />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeSignature}
                onChange={(event) => setIncludeSignature(event.target.checked)}
              />
            }
            label="Include email signature"
          />
          <EmailWYSWIGEditor
            editorRef={editorRef}
            editorReady={editorReady}
            setEditorReady={setEditorReady}
            insertLinkDialogOpen={insertLinkDialogOpen}
            setInsertLinkDialogOpen={setInsertLinkDialogOpen}
            includeSignature={includeSignature}
            signaturePreviewHtml={signaturePreviewHtml}
          />
          {includeSignature && !signaturePreviewHtml ? (
            <Typography variant="caption" color="text.secondary">
              No email signature saved yet. Add one in Settings → Email to
              include it with this message.
            </Typography>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={handleSoftClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSending}
          disabled={!subject}
          onClick={handleSendButtonClicked}
        >
          Send
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}
